import { ResolvedSite } from "resolvers/data/siteResolver";
import { ResolvedVehicleCardSettings } from "resolvers/data/vehicleCardSettingsResolver";
import { ResolvedVehicleDisplaySettings } from "resolvers/pages/pdpPageResolver";
import { ResponseVehicle } from "services/motorpoint/shortlist";
import generateGtmEcommerceEntryString from "utils/generateGtmEcommerceEntryString";

import CarouselView from "./CarouselView";
import GridView from "./GridView";
import useRecommendedVehicles from "./hooks/useRecommendedVehicles";

export enum Visibility {
    ON = "on",
    OFF = "off",
}

export enum Style {
    GRID = "grid",
    CAROUSEL = "carousel",
}

export type RecommendedVehiclesProps = {
    stockBookNumber: string | number;
    shortlistedStocks: number[];
    allStoresDetails: ResolvedSite[];
    components: {
        title: string;
        visibility: Visibility;
        style: Style;
    };
    vehicleCardSettings: ResolvedVehicleCardSettings;
    vehicleDisplaySettings: ResolvedVehicleDisplaySettings;
};

const VIEW_COMPONENTS = {
    [Style.GRID]: GridView,
    [Style.CAROUSEL]: CarouselView,
};

const RecommendedVehiclesView = ({
    stockBookNumber,
    shortlistedStocks,
    components: { title, style },
    allStoresDetails,
    vehicleCardSettings,
    vehicleDisplaySettings,
}: RecommendedVehiclesProps) => {
    const ViewComponent = VIEW_COMPONENTS[style];

    const vehicles = useRecommendedVehicles(stockBookNumber);

    if (!vehicles || !vehicles.length || !ViewComponent) return null;

    const onClick = (vehicle: ResponseVehicle, index: number) => {
        const googlePlaceId = allStoresDetails?.find(
            (store) => store?.siteId === vehicle?.LocationId
        )?.googleMaps?.placeId;
        window.dataLayer = window.dataLayer || [];

        const gtmEventData = generateGtmEcommerceEntryString(
            {
                vehicle,
                itemListId: "pdp_recommended_vehicles",
                itemListName: "PDP Recommended Vehicles",
                itemListPosition: index + 1,
                event: "select_item",
                googlePlaceId,
            },
            true
        );

        window.dataLayer.push(gtmEventData);
    };

    return (
        <ViewComponent
            title={title}
            onClick={onClick}
            vehicles={vehicles}
            shortlistedStocks={shortlistedStocks}
            vehicleCardSettings={vehicleCardSettings}
            vehicleDisplaySettings={vehicleDisplaySettings}
        />
    );
};

const RecommendedVehicles = (props: RecommendedVehiclesProps) => {
    if (props.components.visibility === Visibility.OFF) return null;

    return <RecommendedVehiclesView {...props} />;
};

export default RecommendedVehicles;
